import React, { useState, useEffect, useRef } from 'react';
import styled, { keyframes, css } from 'styled-components';
import useAuthenticatedAxios from '../../auth/AuthenticatedAxios';

// Import modules
import AgentDirectivesModule from './modules/AgentDirectivesModule';
import ScenarioFlowsModule from './modules/ScenarioFlowsModule';
import RuleSetsModule from './modules/RuleSetsModule';
import KnowledgeCategoriesModule from './modules/KnowledgeCategoriesModule';
import ApplicationReviewDirectivesModule from './modules/ApplicationReviewDirectivesModule';
import BrainLearningModule from './modules/BrainLearningModule';
import TrainingModeModule from './modules/TrainingModeModule';
import OpportunityMapsModule from './modules/OpportunityMapsModule';
import ExpertStrategiesModule from './modules/ExpertStrategiesModule';
import PipelineModule from './modules/PipelineModule';
import UltimateBrainGraphModule from './modules/UltimateBrainGraphModule';

// Import AI Assistant
import { AIAssistantProvider, useAIAssistant, AIAssistantPanel } from '../../Components/AIAssistant';

// Import Right Toolbar (contains AI Assistant button + Personalization Brain)
import RightToolbar from '../../Components/RightToolbar';

// ============================================
// SVG ICONS (Simple, monochrome style)
// ============================================

const Icons = {
  robot: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="11" width="18" height="10" rx="2" />
      <circle cx="12" cy="5" r="2" />
      <path d="M12 7v4" />
      <circle cx="8" cy="16" r="1" />
      <circle cx="16" cy="16" r="1" />
    </svg>
  ),
  book: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
      <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
    </svg>
  ),
  flow: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="16 3 21 3 21 8" />
      <line x1="4" y1="20" x2="21" y2="3" />
      <polyline points="21 16 21 21 16 21" />
      <line x1="15" y1="15" x2="21" y2="21" />
      <line x1="4" y1="4" x2="9" y2="9" />
    </svg>
  ),
  list: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="8" y1="6" x2="21" y2="6" />
      <line x1="8" y1="12" x2="21" y2="12" />
      <line x1="8" y1="18" x2="21" y2="18" />
      <line x1="3" y1="6" x2="3.01" y2="6" />
      <line x1="3" y1="12" x2="3.01" y2="12" />
      <line x1="3" y1="18" x2="3.01" y2="18" />
    </svg>
  ),
  clipboard: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2" />
      <rect x="9" y="3" width="6" height="4" rx="1" />
      <path d="M9 12l2 2 4-4" />
    </svg>
  ),
  brain: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9.5 2A2.5 2.5 0 0 1 12 4.5v15a2.5 2.5 0 0 1-4.96.44 2.5 2.5 0 0 1-2.96-3.08 3 3 0 0 1-.34-5.58 2.5 2.5 0 0 1 1.32-4.24 2.5 2.5 0 0 1 1.98-3A2.5 2.5 0 0 1 9.5 2Z"/>
      <path d="M14.5 2A2.5 2.5 0 0 0 12 4.5v15a2.5 2.5 0 0 0 4.96.44 2.5 2.5 0 0 0 2.96-3.08 3 3 0 0 0 .34-5.58 2.5 2.5 0 0 0-1.32-4.24 2.5 2.5 0 0 0-1.98-3A2.5 2.5 0 0 0 14.5 2Z"/>
    </svg>
  ),
  download: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  ),
  plus: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  ),
  settings: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  ),
  beaker: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 3h6v5l4 9a2 2 0 0 1-1.8 2.9H6.8A2 2 0 0 1 5 17l4-9V3z" />
      <path d="M9 3h6" />
    </svg>
  ),
  map: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6" />
      <line x1="8" y1="2" x2="8" y2="18" />
      <line x1="16" y1="6" x2="16" y2="22" />
    </svg>
  ),
  strategy: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2L2 7l10 5 10-5-10-5z"/>
      <path d="M2 17l10 5 10-5"/>
      <path d="M2 12l10 5 10-5"/>
    </svg>
  ),
  pipeline: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="2"/>
      <line x1="3" y1="9" x2="21" y2="9"/>
      <line x1="9" y1="21" x2="9" y2="9"/>
    </svg>
  ),
  ultimateBrain: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3"/>
      <circle cx="12" cy="12" r="8" strokeDasharray="4 2"/>
      <line x1="12" y1="1" x2="12" y2="4"/>
      <line x1="12" y1="20" x2="12" y2="23"/>
      <line x1="1" y1="12" x2="4" y2="12"/>
      <line x1="20" y1="12" x2="23" y2="12"/>
      <line x1="4.22" y1="4.22" x2="6.34" y2="6.34"/>
      <line x1="17.66" y1="17.66" x2="19.78" y2="19.78"/>
      <line x1="4.22" y1="19.78" x2="6.34" y2="17.66"/>
      <line x1="17.66" y1="6.34" x2="19.78" y2="4.22"/>
    </svg>
  ),
};

// ============================================
// ANIMATIONS
// ============================================

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.7; }
`;

// ============================================
// STYLED COMPONENTS - MAIN LAYOUT
// ============================================

const HubContainer = styled.div`
  display: flex;
  height: 100%;
  width: 100%;
  background: linear-gradient(135deg, #0a0f1a 0%, #0f1629 50%, #0a0f1a 100%);
  color: #FFFFFF;
  overflow: hidden;
  position: relative;
  transition: padding-right 0.25s ease;
  
  /* Adjust layout when AI Assistant is pinned */
  ${props => props.isPinned && css`
    padding-right: 420px;
  `}
`;

const Sidebar = styled.div`
  width: 280px;
  min-width: 280px;
  height: 100%;
  background: linear-gradient(180deg, rgba(15, 22, 41, 0.98) 0%, rgba(10, 15, 26, 0.98) 100%);
  border-right: 1px solid rgba(99, 102, 241, 0.15);
  display: flex;
  flex-direction: column;
  position: relative;
  z-index: 10;
`;

const LogoSection = styled.div`
  padding: 24px 20px;
  border-bottom: 1px solid rgba(99, 102, 241, 0.1);
`;

const LogoTitle = styled.h1`
  font-size: 28px;
  font-weight: 700;
  margin: 0;
  background: linear-gradient(135deg, #a5b4fc 0%, #818cf8 50%, #6366f1 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  letter-spacing: -0.5px;
`;

const LogoSubtitle = styled.p`
  font-size: 12px;
  color: rgba(165, 180, 252, 0.6);
  margin: 4px 0 0 0;
  text-transform: uppercase;
  letter-spacing: 2px;
`;

// User Type Badge - shows whether user is in simple or super/power mode
const UserTypeBadge = styled.div`
  margin-top: 12px;
  padding: 4px 10px;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 1px;
  border-radius: 12px;
  text-transform: uppercase;
  
  ${props => props.userType === 'super' ? css`
    background: linear-gradient(135deg, rgba(139, 92, 246, 0.3) 0%, rgba(167, 139, 250, 0.2) 100%);
    border: 1px solid rgba(139, 92, 246, 0.4);
    color: #a78bfa;
  ` : css`
    background: linear-gradient(135deg, rgba(34, 197, 94, 0.3) 0%, rgba(74, 222, 128, 0.2) 100%);
    border: 1px solid rgba(34, 197, 94, 0.4);
    color: #4ade80;
  `}
`;

const ModulesSection = styled.div`
  padding: 20px 12px;
  flex: 1;
  overflow-y: auto;
`;

const SectionLabel = styled.div`
  font-size: 10px;
  color: rgba(165, 180, 252, 0.5);
  text-transform: uppercase;
  letter-spacing: 1.5px;
  padding: 0 12px;
  margin-bottom: 12px;
  font-weight: 600;
`;

const ModuleButton = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 16px;
  margin-bottom: 8px;
  background: ${props => props.active 
    ? 'linear-gradient(135deg, rgba(99, 102, 241, 0.2) 0%, rgba(139, 92, 246, 0.15) 100%)' 
    : 'transparent'};
  border: 1px solid ${props => props.active 
    ? 'rgba(99, 102, 241, 0.4)' 
    : 'transparent'};
  border-radius: 12px;
  color: ${props => props.active ? '#fff' : 'rgba(255, 255, 255, 0.7)'};
  font-size: 14px;
  font-weight: ${props => props.active ? '600' : '500'};
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: left;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 3px;
    background: ${props => props.active 
      ? 'linear-gradient(180deg, #6366f1 0%, #8b5cf6 100%)' 
      : 'transparent'};
    border-radius: 0 2px 2px 0;
  }

  &:hover {
    background: ${props => props.active 
      ? 'linear-gradient(135deg, rgba(99, 102, 241, 0.25) 0%, rgba(139, 92, 246, 0.2) 100%)' 
      : 'rgba(99, 102, 241, 0.08)'};
    color: #fff;
    border-color: ${props => props.active 
      ? 'rgba(99, 102, 241, 0.5)' 
      : 'rgba(99, 102, 241, 0.2)'};
  }

  &:active {
    transform: scale(0.98);
  }
`;

const ModuleIcon = styled.span`
  font-size: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: ${props => props.active 
    ? 'linear-gradient(135deg, rgba(99, 102, 241, 0.3) 0%, rgba(139, 92, 246, 0.2) 100%)' 
    : 'rgba(255, 255, 255, 0.05)'};
  border-radius: 10px;
  transition: all 0.2s ease;

  ${ModuleButton}:hover & {
    background: linear-gradient(135deg, rgba(99, 102, 241, 0.3) 0%, rgba(139, 92, 246, 0.2) 100%);
    transform: scale(1.05);
  }
`;

const ModuleInfo = styled.div`
  flex: 1;
`;

const ModuleName = styled.div`
  font-size: 14px;
  margin-bottom: 2px;
`;

const ModuleDescription = styled.div`
  font-size: 11px;
  color: rgba(165, 180, 252, 0.5);
  font-weight: 400;
`;

const StatusBadge = styled.span`
  font-size: 9px;
  padding: 3px 8px;
  border-radius: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  
  ${props => props.status === 'active' && css`
    background: rgba(16, 185, 129, 0.15);
    color: #10b981;
    border: 1px solid rgba(16, 185, 129, 0.3);
  `}
  
  ${props => props.status === 'new' && css`
    background: rgba(99, 102, 241, 0.15);
    color: #a5b4fc;
    border: 1px solid rgba(99, 102, 241, 0.3);
    animation: ${pulse} 2s ease-in-out infinite;
  `}
  
  ${props => props.status === 'coming' && css`
    background: rgba(251, 191, 36, 0.1);
    color: rgba(251, 191, 36, 0.8);
    border: 1px solid rgba(251, 191, 36, 0.2);
  `}
`;

const SidebarFooter = styled.div`
  padding: 16px 20px;
  border-top: 1px solid rgba(99, 102, 241, 0.1);
  background: rgba(10, 15, 26, 0.5);
`;

const ExpertInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ExpertAvatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  font-weight: 600;
  color: #fff;
`;

const ExpertDetails = styled.div`
  flex: 1;
`;

const ExpertName = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #fff;
`;

const ExpertRole = styled.div`
  font-size: 11px;
  color: rgba(165, 180, 252, 0.5);
`;

// ============================================
// STYLED COMPONENTS - MAIN CONTENT
// ============================================

const MainContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;
`;

const TopBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 24px;
  background: rgba(15, 22, 41, 0.6);
  border-bottom: 1px solid rgba(99, 102, 241, 0.1);
  backdrop-filter: blur(10px);
`;

const PageTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const PageIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  
  svg {
    width: 24px;
    height: 24px;
  }
`;

const PageTitleText = styled.h2`
  font-size: 20px;
  font-weight: 600;
  margin: 0;
  color: #fff;
`;

const PageSubtitle = styled.span`
  font-size: 13px;
  color: rgba(165, 180, 252, 0.6);
  margin-left: 12px;
  padding-left: 12px;
  border-left: 1px solid rgba(99, 102, 241, 0.2);
`;

const TopBarActions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 18px;
  background: ${props => props.primary 
    ? 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)' 
    : 'rgba(99, 102, 241, 0.1)'};
  border: 1px solid ${props => props.primary 
    ? 'transparent' 
    : 'rgba(99, 102, 241, 0.3)'};
  border-radius: 10px;
  color: ${props => props.primary ? '#fff' : '#a5b4fc'};
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: ${props => props.primary 
      ? '0 4px 15px rgba(99, 102, 241, 0.4)' 
      : '0 4px 15px rgba(99, 102, 241, 0.15)'};
    background: ${props => props.primary 
      ? 'linear-gradient(135deg, #818cf8 0%, #a78bfa 100%)' 
      : 'rgba(99, 102, 241, 0.15)'};
  }

  &:active {
    transform: translateY(0);
  }
`;

const ContentArea = styled.div`
  flex: 1;
  overflow: hidden;
  animation: ${fadeIn} 0.3s ease;
`;

// ============================================
// MODULE DEFINITIONS
// ============================================

// Module definitions with visibility control
// superOnly: true = only visible to super users (power users)
// superOnly: false/undefined = visible to all users (simple & super)
const ALL_MODULES = [
  {
    id: 'ultimate-brain',
    name: 'Ultimate Brain',
    description: 'Visual brain connections graph',
    icon: Icons.ultimateBrain,
    status: 'new',
    component: UltimateBrainGraphModule,
    superOnly: false,
  },
  {
    id: 'brain-learning',
    name: 'Brain Learning',
    description: 'AI insights & improvement suggestions',
    icon: Icons.brain,
    status: 'active',
    component: BrainLearningModule,
    superOnly: false, // VISIBLE to simple users (shows AI suggestions)
  },
  {
    id: 'training-mode',
    name: 'Training Mode',
    description: 'Teach your AI through scenarios',
    icon: Icons.beaker,
    status: 'active',
    component: TrainingModeModule,
    superOnly: false, // VISIBLE to simple users (allows rating conversations)
  },
  {
    id: 'opportunity-maps',
    name: 'Opportunity Maps',
    description: 'Define pathways & alternatives',
    icon: Icons.map,
    status: 'active',
    component: OpportunityMapsModule,
    superOnly: true, // HIDDEN from simple users
  },
  {
    id: 'knowledge-categories',
    name: 'Knowledge Categories',
    description: 'Context orchestration & classification',
    icon: Icons.book,
    status: 'active',
    component: KnowledgeCategoriesModule,
    superOnly: true, // HIDDEN from simple users
  },
  {
    id: 'scenario-flows',
    name: 'Scenario Flows',
    description: 'Visual flow builder for cases',
    icon: Icons.flow,
    status: 'active',
    component: ScenarioFlowsModule,
    superOnly: true, // HIDDEN from simple users
  },
  {
    id: 'rule-sets',
    name: 'Rule Sets Library',
    description: 'Manage generated rules',
    icon: Icons.list,
    status: 'active',
    component: RuleSetsModule,
    superOnly: true, // HIDDEN from simple users
  },
  {
    id: 'agent-directives',
    name: 'Agent Directives',
    description: 'Configure AI agent behavior',
    icon: Icons.robot,
    status: 'active',
    component: AgentDirectivesModule,
    superOnly: false, // VISIBLE to simple users (allows training directives)
  },
  {
    id: 'expert-strategies',
    name: 'Expert Strategies',
    description: 'Creative workarounds & alternative paths',
    icon: Icons.strategy,
    status: 'active',
    component: ExpertStrategiesModule,
    superOnly: false, // VISIBLE to simple users (allows creating strategies)
  },
  {
    id: 'pipelines',
    name: 'Pipelines',
    description: 'Define case progress stages',
    icon: Icons.pipeline,
    status: 'active',
    component: PipelineModule,
    superOnly: false, // VISIBLE to simple users (allows creating pipelines)
  },
  {
    id: 'application-review',
    name: 'Application Review',
    description: 'Configure validation directives',
    icon: Icons.clipboard,
    status: 'active',
    component: ApplicationReviewDirectivesModule,
    superOnly: true, // HIDDEN from simple users
  },
];

// ============================================
// INNER COMPONENT (uses AI Assistant context)
// ============================================

const SynthoraHubContent = () => {
  const [activeModule, setActiveModule] = useState(() => {
    // Deep link from a Test Bench finding: /Synthora?module=opportunity-maps
    try {
      const m = new URLSearchParams(window.location.search).get('module');
      if (m && ALL_MODULES.some(x => x.id === m)) return m;
    } catch { /* ignore */ }
    return 'ultimate-brain';
  });
  const [userType, setUserType] = useState('super'); // Default to super, will be updated from API
  const [userTypeLoading, setUserTypeLoading] = useState(true);
  // Power mode (switched on only in the CRM): the brain-editing modules. Without it, Synthora holds the
  // case tools only; the agent itself is built in "My agent" (/agent/build).
  const [powerMode, setPowerMode] = useState(false);
  const { updateContext, isPinned, isPanelOpen, togglePanel, conversationHistory } = useAIAssistant();
  const trainingModeRef = useRef(null);
  const axios = useAuthenticatedAxios();
  
  // Fetch user type from profile API on mount
  useEffect(() => {
    const fetchUserType = async () => {
      try {
        const response = await axios.get('/profile');
        if (response.data?.profile) {
          setUserType(response.data.profile.userType || 'super');
          setPowerMode(!!response.data.profile.powerMode);
        }
      } catch (error) {
        console.error('Failed to fetch user type:', error);
        // Default to super if fetch fails (so nothing is hidden)
      } finally {
        setUserTypeLoading(false);
      }
    };
    
    if (axios) {
      fetchUserType();
    }
  }, [axios]);
  
  // Filter modules based on user type
  // Simple users: only see modules where superOnly is false
  // Super users: see all modules
  const CASE_TOOLS = ['pipelines', 'application-review'];
  const MODULES = !powerMode
    ? ALL_MODULES.filter(m => CASE_TOOLS.includes(m.id))
    : userType === 'simple'
      ? ALL_MODULES.filter(m => !m.superOnly)
      : ALL_MODULES;
  
  const currentModule = MODULES.find(m => m.id === activeModule);
  const ModuleComponent = currentModule?.component;
  
  // Reset active module if current one is hidden
  useEffect(() => {
    // Wait for the profile: before it arrives only the case tools show, and a deep link must survive that.
    if (!userTypeLoading && !currentModule && MODULES.length > 0) {
      setActiveModule(MODULES[0].id);
    }
  }, [currentModule, MODULES, userTypeLoading]);

  // Update AI Assistant context when module changes
  useEffect(() => {
    if (currentModule) {
      updateContext({
        currentModule: currentModule.id,
        moduleName: currentModule.name,
        moduleDescription: currentModule.description,
        selectedElements: {},
        formState: {},
        availableActions: [],
      });
    }
  }, [activeModule, currentModule, updateContext]);

  return (
    <HubContainer isPinned={isPinned}>
      {/* Sidebar Navigation */}
      <Sidebar>
        <LogoSection>
          <LogoTitle>Synthora</LogoTitle>
          <LogoSubtitle>Personalization Hub</LogoSubtitle>
          {/* Show user mode badge */}
          {!userTypeLoading && (
            <UserTypeBadge userType={powerMode ? userType : 'simple'}>
              {powerMode ? '⚡ Power Mode' : '🧰 Case tools'}
            </UserTypeBadge>
          )}
          {!userTypeLoading && !powerMode && (
            <a href="/agent/build" style={{ display: 'block', marginTop: 10, fontSize: 12, lineHeight: 1.45, color: '#79c0ff' }}>
              Your agent is now built with the Agent Builder in “My agent” →
            </a>
          )}
        </LogoSection>

        <ModulesSection>
          <SectionLabel>Modules</SectionLabel>
          
          {MODULES.map((module) => (
            <ModuleButton
              key={module.id}
              active={activeModule === module.id}
              onClick={() => setActiveModule(module.id)}
            >
              <ModuleIcon active={activeModule === module.id}>
                {module.icon}
              </ModuleIcon>
              <ModuleInfo>
                <ModuleName>{module.name}</ModuleName>
                <ModuleDescription>{module.description}</ModuleDescription>
              </ModuleInfo>
              <StatusBadge status={module.status}>
                {module.status === 'active' ? '●' : module.status === 'new' ? 'NEW' : 'SOON'}
              </StatusBadge>
            </ModuleButton>
          ))}
        </ModulesSection>

        <SidebarFooter>
          <ExpertInfo>
            <ExpertAvatar>E</ExpertAvatar>
            <ExpertDetails>
              <ExpertName>Expert Mode</ExpertName>
              <ExpertRole>Full Access</ExpertRole>
            </ExpertDetails>
          </ExpertInfo>
        </SidebarFooter>
      </Sidebar>

      {/* Main Content Area */}
      <MainContent>
        <TopBar>
          <PageTitle>
            <PageIcon>{currentModule?.icon}</PageIcon>
            <PageTitleText>{currentModule?.name}</PageTitleText>
            <PageSubtitle>{currentModule?.description}</PageSubtitle>
          </PageTitle>

          <TopBarActions>

            {activeModule === 'rule-sets' && (
              <ActionButton primary>
                {Icons.plus} New Rule Set
              </ActionButton>
            )}
            {activeModule === 'agent-directives' && (
              <ActionButton>
                {Icons.settings} Settings
              </ActionButton>
            )}
            {activeModule === 'knowledge-categories' && (
              <ActionButton primary>
                {Icons.beaker} Test Context Manager
              </ActionButton>
            )}
            {activeModule === 'training-mode' && (
              <ActionButton primary onClick={() => trainingModeRef.current?.openNewSessionModal()}>
                {Icons.plus} New Training Session
              </ActionButton>
            )}
          </TopBarActions>
        </TopBar>

        <ContentArea key={activeModule}>
          {ModuleComponent && (
            activeModule === 'training-mode' 
              ? <ModuleComponent ref={trainingModeRef} />
              : <ModuleComponent />
          )}
        </ContentArea>
      </MainContent>

      {/* Right Toolbar - Contains AI Assistant & Brain buttons */}
      <RightToolbar 
        isPanelPinned={isPinned}
        isAssistantOpen={isPanelOpen}
        onToggleAssistant={togglePanel}
        hasNewMessages={conversationHistory.length === 0}
      />

      {/* AI Assistant Panel (floating panel only, button is in toolbar) */}
      <AIAssistantPanel />
    </HubContainer>
  );
};

// ============================================
// MAIN COMPONENT (with Provider wrapper)
// ============================================

const SynthoraHub = () => {
  return (
    <AIAssistantProvider>
      <SynthoraHubContent />
    </AIAssistantProvider>
  );
};

export default SynthoraHub;

